import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Card,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  List,
  ListItem,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import BusinessIcon from "@mui/icons-material/Business";
import CoffeeIcon from "@mui/icons-material/Coffee";
import StarIcon from "@mui/icons-material/Star";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { CompanyInfo } from "./SettingsLayout";
import CheckIcon from "@mui/icons-material/Check";
import CancelIcon from "@mui/icons-material/Cancel";
import { useNavigate } from "react-router-dom";
import { URLS } from "../../constants";
import api from "../../utils/api";
import {
  getCustomerPaymentMethod,
  getLoggedinUserEmail,
} from "../../api/apiCore";
import { toast } from "react-toastify";
import logger from "../../utils/logger";

const onlyDigits = (s: string) => s.replace(/\D/g, "");
const genIdemKey = () =>
  window.crypto && "randomUUID" in window.crypto
    ? window.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

type PlanId = "freemium" | "basic" | "premium" | "gold";

interface PlanConfig {
  inherits?: PlanId;
  featuresKeys: string[];
  extraFeaturesKeys?: string[];
  titleKey: string;
  msgKey: string;
  priceKey: string;
  priceId: string;
}

const PLAN_CONFIG: Record<PlanId, PlanConfig> = {
  freemium: {
    titleKey: "plan.freemium.title",
    msgKey: "plan.freemium.messages",
    priceKey: "plan.freemium.price",
    featuresKeys: [
      "unlimitedUsers",
      "smartHubAccess",
      "crmIntegration",
      "humanizedSupport",
      "conversationMemory",
      "autoCrmQualification",
      "googleCalendarScheduling",
      "basicManagerInterface",
      "teamActionInterface",
      "reportsAndMetrics",
    ],
    priceId: "price_1S2eNeLc8a84Lk51L7flNNOz",
  },
  basic: {
    titleKey: "plan.basic.title",
    msgKey: "plan.basic.messages",
    priceKey: "plan.basic.price",
    featuresKeys: [
      "unlimitedUsers",
      "smartHubAccess",
      "crmIntegration",
      "humanizedSupport",
      "imageInterpretation",
      "conversationMemory",
      "autoCrmQualification",
      "googleCalendarScheduling",
      "tokenIntegration2",
      "basicManagerInterface",
      "teamActionInterface",
      "reportsAndMetrics",
      "supportPriorityStandard",
    ],
    priceId: "price_1S2XnKLc8a84Lk51pZQ5vIXG",
  },
  premium: {
    titleKey: "plan.premium.title",
    msgKey: "plan.premium.messages",
    priceKey: "plan.premium.price",
    featuresKeys: [
      "unlimitedUsers",
      "smartHubAccess",
      "crmIntegration",
      "humanizedSupport",
      "imageInterpretation",
      "conversationMemory",
      "autoCrmQualification",
      "googleCalendarScheduling",
      "tokenIntegration4",
      "advancedManagerInterface",
      "teamActionInterface",
      "reportsAndMetrics",
      "supportPriorityMedium",
    ],
    priceId: "price_1S2eO2Lc8a84Lk51uaXm1879",
  },
  gold: {
    titleKey: "plan.gold.title",
    msgKey: "plan.gold.messages",
    priceKey: "plan.gold.price",
    featuresKeys: [
      "unlimitedUsers",
      "smartHubAccess",
      "crmIntegration",
      "humanizedSupport",
      "imageInterpretation",
      "conversationMemory",
      "autoCrmQualification",
      "googleCalendarScheduling",
      "tokenIntegrationUnlimited",
      "completeManagerInterface",
      "teamActionWithSummaries",
      "reportsAndMetrics",
      "supportPriorityHigh",
    ],
    priceId: "price_1S2eOOLc8a84Lk51iR9zHmK6",
  },
};
const PLAN_IDS: PlanId[] = ["freemium", "basic", "premium", "gold"];

interface PricingPageProps {
  companyInfo?: CompanyInfo | null;
}

export default function PricingPage({ companyInfo }: PricingPageProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));

  // state for the user's current plan
  const [userPlan, setUserPlan] = useState<PlanId | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<PlanId | null>(null);
  const [openCompare, setOpenCompare] = useState(false);

  // get current companyInfo plan
  useEffect(() => {
    if (companyInfo?.subscription) {
      const subscriptionToPlan: Record<string, PlanId> = {
        freemium: "freemium",
        basic: "basic",
        premium: "premium",
        gold: "gold",
      };

      const currentPlan =
        subscriptionToPlan[companyInfo.subscription.toLowerCase()] ||
        "freemium";
      setUserPlan(currentPlan);
      if (selectedPlan === null) {
        setSelectedPlan(currentPlan);
      }
    } else {
      setUserPlan("freemium");
      if (selectedPlan === null) {
        setSelectedPlan("freemium");
      }
    }
  }, [companyInfo, selectedPlan]);

  const [hoveredPlan, setHoveredPlan] = useState<PlanId | null>(null);

  // deal with plan selection
  const handlePlanSelection = (planId: PlanId) => {
    setSelectedPlan(planId);
  };

  const handleSubscription = async (priceId: string) => {
    try {
      const idem = genIdemKey();
      const body = {
        priceId: priceId,
        customerId: "",
        customerEmail: "",
        trialPeriodDays: 0,
        metadata: { cpf: "", acceptFallback: "0" },
        // metadata: { cpf: onlyDigits(cpf || ""), acceptFallback: acceptFallback ? "1" : "0" },
      };

      await api.post(`${URLS.CLIENT_URL}/api/payment/subscription`, body, {
        headers: {
          "X-Idempotency-Key": idem,
        },
      });
    } catch (error: unknown) {
      logger.error("Error on handleSubscription", error);
    }
  };

  const handleContinue = async (planPriceId: string) => {
    const customerEmail = getLoggedinUserEmail();
    const customerPaymentMethod = await getCustomerPaymentMethod(customerEmail);

    if (customerPaymentMethod.data) {
      handleSubscription(planPriceId);
      toast.success(t("pricing.updatedPlan"));
    } else {
      navigate(`/manage-payment?priceId=${planPriceId}`);
    }
  };

  const allFeatures = useMemo(() => {
    const featureSet = new Set<string>();
    PLAN_IDS.forEach((planId) => {
      PLAN_CONFIG[planId].featuresKeys.forEach((feature) => {
        featureSet.add(feature);
      });
    });
    return Array.from(featureSet);
  }, []);

  const plans = useMemo(
    () =>
      PLAN_IDS.map((id) => {
        const cfg = PLAN_CONFIG[id];
        return {
          id,
          title: t(cfg.titleKey),
          messages: t(cfg.msgKey),
          price: t(cfg.priceKey),
          features: cfg.featuresKeys.map((k) => t(`feature.${k}`)),
          priceId: cfg.priceId,
        };
      }),
    [t],
  );

  const iconMap: Record<PlanId, React.ReactNode> = {
    freemium: <RocketLaunchIcon fontSize="small" />,
    basic: <BusinessIcon fontSize="small" />,
    premium: <CoffeeIcon fontSize="small" />,
    gold: <StarIcon fontSize="small" />,
  };

  return (
    <Box
      sx={{
        maxWidth: { xs: 1200, md: 1400 },
        mx: "auto",
        display: "grid",
        gap: 1,
        px: { xs: 2, md: 4 },
        py: { xs: 2, md: 3 },
      }}
    >
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 3 }}>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 64,
            height: 64,
            borderRadius: "50%",
            background: "linear-gradient(135deg, #34A853 0%, #71c8aa 100%)",
            mb: 2,
          }}
        >
          {selectedPlan && selectedPlan !== userPlan ? (
            <SwapHorizIcon sx={{ fontSize: 32, color: "white" }} />
          ) : (
            <CreditCardIcon sx={{ fontSize: 32, color: "white" }} />
          )}
        </Box>
        <Typography
          variant="h5"
          sx={{ fontWeight: 600, color: theme.palette.primary.main, mb: 1 }}
        >
          {selectedPlan && selectedPlan !== userPlan
            ? t("pricing.changePlan")
            : t("setting.subscription")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {selectedPlan && selectedPlan !== userPlan
            ? t("pricing.changePlanDescription")
            : t("pricing.subscriptionDescription")}
        </Typography>
      </Box>

      {/* botão para comparar */}
      <Box
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "center",
          position: "relative",
          zIndex: 100,
        }}
      >
        <Button
          onClick={() => setOpenCompare(true)}
          variant="contained"
          size="small"
          sx={{
            textTransform: "none",
            borderRadius: 16,
            background: "linear-gradient(90deg, #71c8aa, #34A853)",
            color: "#fff",
            px: 2,
            py: 0.5,
            fontSize: "0.75rem",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            "&:hover": {
              background: "linear-gradient(90deg, #5fb799, #2d9047)",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            },
          }}
        >
          {t("pricing.compareBtn")}
        </Button>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: { xs: "wrap", md: "nowrap" },
          gap: 2,
          justifyContent: "center",
        }}
      >
        {plans.map((plan) => (
          <Box
            key={plan.id}
            sx={{
              display: "flex",
              justifyContent: "center",
              width: {
                xs: "100%",
                sm: "calc(50% - 8px)",
                md: "calc(25% - 12px)",
              },
              minWidth: { md: 280 },
              flex: { md: 1 },
            }}
          >
            <Card
              onClick={() => handlePlanSelection(plan.id)}
              onMouseEnter={() => setHoveredPlan(plan.id)}
              onMouseLeave={() => setHoveredPlan(null)}
              sx={{
                width: "100%",
                maxWidth: { md: 300 },
                height: { xs: "auto", md: "450px" },
                borderRadius: 3,
                backgroundColor: alpha(theme.palette.common.white, 0.2),
                backdropFilter: "blur(10px)",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                cursor: "pointer",
                transition: "all 0.3s ease",
                position: "relative",
                // Highlight only the selected plan
                ...(selectedPlan === plan.id && {
                  boxShadow: `0 0 20px ${alpha(theme.palette.primary.main, 0.3)}`,
                  border: `3px solid ${theme.palette.primary.main}`,
                  transform: "scale(1.02)",
                }),
                "&:hover": {
                  transform:
                    selectedPlan === plan.id ? "scale(1.02)" : "scale(1.01)",
                  boxShadow:
                    selectedPlan === plan.id
                      ? `0 0 25px ${alpha(theme.palette.primary.main, 0.4)}`
                      : theme.shadows[8],
                },
                "&:hover .inner": {
                  background: "linear-gradient(90deg, #34A853, #71c8aa)",
                  color: theme.palette.common.white,
                },
                "&:hover .pill": {
                  backgroundColor: theme.palette.common.white,
                  color: theme.palette.primary.main,
                },
                "&:hover .iconWrapper": {
                  bgcolor: theme.palette.common.white,
                  "& .MuiSvgIcon-root": { color: theme.palette.primary.main },
                },
              }}
            >
              <Box
                className="inner"
                component={motion.div}
                sx={{
                  position: "relative",
                  m: 1.5,
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.common.white, 0.3),
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  flex: "0 0 auto",
                  gap: 2,
                  transition: "all 0.3s ease",
                  // Style for selected (but not current) plan
                  ...(selectedPlan === plan.id &&
                    userPlan !== plan.id && {
                      background: "linear-gradient(90deg, #34A853, #71c8aa)",
                      color: theme.palette.common.white,
                    }),
                  ...(userPlan === plan.id && {
                    background: "linear-gradient(90deg, #34A853, #71c8aa)",
                    color: theme.palette.common.white,
                  }),
                }}
              >
                <Box
                  className="iconWrapper"
                  sx={{
                    position: "absolute",
                    top: 16,
                    left: 16,
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    bgcolor:
                      selectedPlan === plan.id || userPlan === plan.id
                        ? "common.white"
                        : "primary.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color:
                      selectedPlan === plan.id || userPlan === plan.id
                        ? "primary.main"
                        : "common.white",
                    transition: "0.3s",
                  }}
                >
                  {iconMap[plan.id]}
                </Box>

                <Box
                  className="pill"
                  sx={{
                    position: "absolute",
                    top: 16,
                    right: 16,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1.5,
                    backgroundColor:
                      selectedPlan === plan.id || userPlan === plan.id
                        ? theme.palette.common.white
                        : theme.palette.primary.main,
                    color:
                      selectedPlan === plan.id || userPlan === plan.id
                        ? theme.palette.primary.main
                        : "#fff",
                    transition: "0.3s",
                    fontSize: "0.75rem",
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                    {plan.title}
                  </Typography>
                </Box>

                <Typography
                  variant="h6"
                  fontWeight="bold"
                  color="inherit"
                  sx={{ mt: 5, mb: 1, fontSize: "1.4rem" }}
                >
                  {plan.price}
                </Typography>
                <Typography
                  variant="body2"
                  color="inherit"
                  sx={{ opacity: 0.7, fontSize: "0.8rem" }}
                >
                  {plan.messages}
                </Typography>
              </Box>

              <Box
                sx={{
                  flex: "1 1 auto",
                  px: 2,
                  overflowY: "auto",
                  mb: 2,
                  maxHeight: "150px",
                  "&::-webkit-scrollbar": { width: 6 },
                  "&::-webkit-scrollbar-track": { background: "transparent" },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: alpha("#000", 0.2),
                    borderRadius: 3,
                  },
                }}
              >
                {plan.features.map((feat) => (
                  <Typography
                    key={feat}
                    variant="body2"
                    sx={{ fontSize: "0.75rem", mb: 0.5 }}
                  >
                    • {feat}
                  </Typography>
                ))}
              </Box>

              <Box sx={{ px: 2, pb: 2 }}>
                {userPlan === plan.id ? (
                  <Box
                    sx={{
                      textAlign: "center",
                      py: 1.5,
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                      fontSize: "0.9rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                    }}
                  >
                    <CheckIcon sx={{ fontSize: 18 }} />
                    {t("pricing.currentPlan")}
                  </Box>
                ) : selectedPlan === plan.id ? (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => {
                        handleContinue(plan.priceId);
                      }}
                      sx={{
                        fontSize: "0.8rem",
                        py: 1,
                        backgroundColor: theme.palette.primary.main,
                        "&:hover": {
                          backgroundColor: theme.palette.primary.dark,
                        },
                      }}
                    >
                      {t("pricing.continue")}
                    </Button>
                    <Typography
                      variant="caption"
                      sx={{
                        textAlign: "center",
                        color: theme.palette.primary.main,
                        fontWeight: 500,
                      }}
                    >
                      ✓ {t("pricing.planSelected")}
                    </Typography>
                  </Box>
                ) : hoveredPlan === plan.id ? (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Button
                      fullWidth
                      variant="contained"
                      sx={{ fontSize: "0.8rem", py: 1 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePlanSelection(plan.id);
                      }}
                    >
                      {t("pricing.choosePlan")}
                    </Button>
                    <Button
                      fullWidth
                      variant="outlined"
                      sx={{
                        fontSize: "0.75rem",
                        py: 0.8,
                        color: theme.palette.primary.main,
                        borderColor: theme.palette.primary.main,
                        "&:hover": {
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.1,
                          ),
                          borderColor: theme.palette.primary.main,
                        },
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenCompare(true);
                      }}
                    >
                      {t("pricing.moreDetails")}
                    </Button>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      textAlign: "center",
                      py: 1.5,
                      color: theme.palette.text.secondary,
                      fontSize: "0.8rem",
                      fontStyle: "italic",
                    }}
                  >
                    {t("pricing.clickToSelect")}
                  </Box>
                )}
              </Box>
            </Card>
          </Box>
        ))}
      </Box>

      <AnimatePresence>
        {openCompare && (
          <Dialog
            open
            onClose={() => setOpenCompare(false)}
            fullWidth
            maxWidth={isSm ? "xs" : "md"}
            PaperProps={{
              sx: {
                backgroundColor: alpha(theme.palette.common.white, 0.9),
                backdropFilter: "blur(10px)",
                borderRadius: 3,
                p: 2,
                maxHeight: "80vh",
                "&::-webkit-scrollbar": { width: 6 },
                "&::-webkit-scrollbar-track": { background: "transparent" },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: alpha("#000", 0.2),
                  borderRadius: 3,
                },
              },
            }}
          >
            <DialogTitle
              sx={{ textAlign: "center", position: "relative", pb: 1 }}
            >
              <IconButton
                aria-label="close"
                onClick={() => setOpenCompare(false)}
                sx={{ position: "absolute", right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
              <Typography variant="h5" fontWeight="bold">
                {t("pricing.compareTitle")}
              </Typography>
            </DialogTitle>
            <DialogContent sx={{ py: 1, maxHeight: "65vh", overflowY: "auto" }}>
              {isSm ? (
                <List>
                  {plans.map((p) => (
                    <Box key={p.id} sx={{ mb: 2 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mb: 1,
                          gap: 1,
                        }}
                      >
                        {iconMap[p.id]}
                        <Typography variant="subtitle1" fontWeight="bold">
                          {p.title}
                        </Typography>
                      </Box>
                      {allFeatures.map((f) => {
                        const label = t(`feature.${f}`);
                        const has = p.features.includes(label);
                        return (
                          <ListItem key={f} sx={{ py: 0.5 }}>
                            {has ? (
                              <CheckIcon color="success" fontSize="small" />
                            ) : (
                              <CancelIcon color="error" fontSize="small" />
                            )}
                            <Typography
                              variant="body2"
                              sx={{ ml: 1, fontSize: "0.8rem" }}
                            >
                              {label}
                            </Typography>
                          </ListItem>
                        );
                      })}
                    </Box>
                  ))}
                </List>
              ) : (
                <Box component="table" sx={{ width: "100%" }}>
                  <Box component="thead">
                    <Box component="tr">
                      <Box component="th" />
                      {plans.map((p) => (
                        <Box
                          component="th"
                          key={p.id}
                          sx={{
                            borderRight: (t) =>
                              `1px solid ${alpha(t.palette.text.primary, 0.2)}`,
                            py: 1,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 1,
                            }}
                          >
                            {iconMap[p.id]}
                            <Typography variant="subtitle2" fontWeight="bold">
                              {p.title}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  <Box component="tbody">
                    {allFeatures.map((featureKey) => {
                      const label = t(`feature.${featureKey}`);
                      return (
                        <Box
                          component="tr"
                          key={featureKey}
                          sx={{
                            "&:nth-of-type(odd)": {
                              backgroundColor: alpha("#000", 0.02),
                            },
                            "& td, & th": { borderColor: alpha("#000", 0.1) },
                          }}
                        >
                          <Box component="td" sx={{ py: 1, px: 1 }}>
                            {label}
                          </Box>
                          {plans.map((p) => {
                            const has = p.features.includes(label);
                            return (
                              <Box
                                component="td"
                                key={p.id}
                                sx={{ textAlign: "center", py: 1 }}
                              >
                                {has ? (
                                  <CheckIcon color="success" fontSize="small" />
                                ) : (
                                  <CancelIcon color="error" fontSize="small" />
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ justifyContent: "center", py: 1 }}>
              <Button onClick={() => setOpenCompare(false)} variant="contained">
                {t("pricing.close")}
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </AnimatePresence>
    </Box>
  );
}
